import {
  BaseButton,
  BaseButtonProps,
  Image,
  Text,
  View,
  useThemeProvider,
} from "@towersystems/roam-common-native";
import React, { useImperativeHandle } from "react";
import { TextStyle } from "react-native";

export type QuickKeyTileButtonProps = Omit<BaseButtonProps, "label"> & {
  wrapperStyle?: BaseButtonProps["style"];
  isDestructive?: boolean;
  isAction?: boolean;
  label?: string | React.ReactNode;
  backgroundImage?: string;
  iconImageUrl?: string;
  labelStyle?: TextStyle;
  icon?: React.ReactNode;
};

export const QuickKeyTileButton = React.forwardRef<
  any,
  QuickKeyTileButtonProps
>(
  (
    {
      children,
      fullWidth,
      style,
      label,
      icon,
      isDestructive,
      isAction,
      disabled,
      backgroundImage,
      wrapperStyle,
      labelStyle,
      onPress,
      iconImageUrl,
      ...p
    },
    ref
  ) => {
    const { spacings, colors } = useThemeProvider();

    const color = p.color || colors.text;
    const backgroundColor = p.backgroundColor || colors.neutralButton;

    let textColor = color;

    const backgroundImageMarkup = backgroundImage ? (
      <View
        style={{
          zIndex: -1,
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "hidden",
        }}
      >
        <Image
          defaultSource={{ uri: backgroundImage }}
          style={{ width: 400, height: undefined, flex: 1 }}
          overlayColor="rgba(0,0,0,0.35)"
          overlayType="solid"
        />
      </View>
    ) : undefined;

    const iconImageUrlMarkup = iconImageUrl ? (
      <View
        style={{
          zIndex: -1,
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 30,
          justifyContent: "center",
          alignItems: "center",
          // overflow: 'visible' (default) to allow “busting out”
        }}
      >
        <Image
          source={{ uri: iconImageUrl }}
          overlayColor="rgba(255,255,255,255,0.1)"
          overlayType="solid"
          style={{
            height: 160,
            width: 160,
            // The following two lines center the image, since View is flex:
            // alignSelf: "center",
            // justifyContent: "center",
          }}
          resizeMode="cover"
        />
      </View>
    ) : undefined;

    const isDisabled = disabled;

    if (isDisabled) {
      textColor = colors.Text;
    } else if (isDestructive) {
      textColor = colors.destructive;
    } else if (isAction) {
      textColor = colors.action;
    }

    const labelMarkup = label ? (
      <View>
        <Text
          style={[
            {
              color: textColor,
              fontFamily: "Gilmer-Bold",
              fontSize: 19,
              opacity: isDisabled ? 0.5 : 1,
            },
            isDisabled && { color: colors.Text },
            labelStyle,
          ]}
        >
          {label}
        </Text>
      </View>
    ) : undefined;

    // this ignores button prop:disabled
    /*
    useImperativeHandle(ref, () => ({
      tilePress,
    }));
    */

    function tilePress() {
      if (onPress && !isDisabled) {
        onPress();
      }
    }

    return (
      <BaseButton
        style={[
          {
            width: fullWidth ? "100%" : undefined,
            minHeight: 60,
            borderColor: "#00000012",
            borderWidth: 2,
          },
          style,
          fullWidth && { flexGrow: 1 },
          isDisabled && { opacity: 0.3 },
        ]}
        {...p}
        onPress={!disabled ? tilePress : undefined}
        backgroundColor={backgroundColor}
        disabled={isDisabled}
      >
        <View
          style={[
            {
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
            },
            wrapperStyle,
          ]}
          flex
        >
          {icon ? (
            <View
              style={{
                marginBottom: spacings.s4,
              }}
            >
              {icon}
            </View>
          ) : null}
          {labelMarkup}
          {children}
        </View>
        {backgroundImageMarkup}
        {iconImageUrlMarkup}
      </BaseButton>
    );
  }
);
